import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {LayoutHighlightedBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-highlighted-background-option';
import {LayoutHighlightedTextColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-highlighted-text-color-option';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {LayoutHighlightedBackgroundOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-highlighted-background-opacity-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import styles from './poster-editor-item-date-styles-panel.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromTableItem} from '@Hooks/poster-editor/useGetKeyFromTableItem';

interface PosterEditorItemDateStylesPanelProps {
  panelId: string;
}

enum DateStylesPanel {
  DEFAULT = 'DEFAULT',
  HIGHLIGHTED_BACKGROUND = 'HIGHLIGHTED_BACKGROUND',
  HIGHLIGHTED_TEXT = 'HIGHLIGHTED_TEXT',
}

export default function PosterEditorItemDateStylesPanel({...props}: PosterEditorItemDateStylesPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const [panelToShow, setPanelToShow] = useState(DateStylesPanel.DEFAULT);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const highlightedTextColor = useGetKeyFromTableItem('highlightedTextColor');
  const highlightedBgColor = useGetKeyFromTableItem('highlightedBackgroundColor');

  useEffect((): void => {
    if (panelToShow === DateStylesPanel.DEFAULT) {
      updateOptionsPanelHeight(props.panelId, '236px');
    }
  }, [panelToShow]);

  const showHighlightStyles = (): boolean => {
    if (!activeItem) {
      return false;
    }
    if (!activeItem.isTableItem()) {
      return false;
    }

    if (activeItem.isSchedule()) {
      return activeItem.doesItemHaveDateOptions();
    }

    return activeItem.getHighlightedRowsData().rows.length > 0;
  };
  useUpdateEffect((): void => {
    onClose();
  }, [activeItem]);

  useEffect((): void => {
    if (!activeItem) {
      return;
    }
    if (!showHighlightStyles()) {
      onClose();
    }
  }, [activeItem]);

  if (!activeItem) {
    return null;
  }

  if (!activeItem.isTableItem()) {
    return null;
  }

  if (!showHighlightStyles()) {
    return null;
  }

  if (!highlightedTextColor || !highlightedBgColor) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={activeItem.isSchedule() ? window.i18next.t('pmwjs_highlighted_items') : window.i18next.t('pmwjs_date_styles')} />;
  };

  const getContent = (): ReactElement => {
    return (
      <div className={`flex-v-row spacing-p-t-1 spacing-p-r-6 spacing-p-l-6 spacing-p-b-6 ${styles.gap}`}>
        <LayoutHighlightedBackgroundOpacityOption />
        <LayoutHighlightedTextColorOption
          onClickColorFill={(): void => {
            setPanelToShow(DateStylesPanel.HIGHLIGHTED_TEXT);
          }}
        />
        <LayoutHighlightedBackgroundOption
          label={activeItem.isTableItem() ? window.i18next.t('pmwjs_row_color') : undefined}
          onClickColorFill={(): void => {
            setPanelToShow(DateStylesPanel.HIGHLIGHTED_BACKGROUND);
          }}
        />
      </div>
    );
  };

  const getDefaultPanel = (): ReactElement => {
    return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
  };

  const onChangeHighlightedBackgroundColor = (highlightedBackgroundColor: RGBA[], undoable = true): void => {
    activeItem.updateHighlightedBackgroundColor(highlightedBackgroundColor[0], undoable);
  };

  const onChangeHighlightedTextColor = (highlightedTextColor: RGBA[], undoable = true): void => {
    activeItem.updateHighlightedTextColor(highlightedTextColor[0], undoable);
  };

  const getHighlightedBackgroundColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        panelId={props.panelId}
        fillObject={{
          fillColor: [highlightedBgColor],
          fillType: FillTypes.SOLID,
        }}
        onChangeColor={onChangeHighlightedBackgroundColor}
        onBackButton={(): void => {
          setPanelToShow(DateStylesPanel.DEFAULT);
        }}
        onClose={onClose}
      />
    );
  };

  const getHighlightedTextColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        panelId={props.panelId}
        fillObject={{
          fillColor: [highlightedTextColor],
          fillType: FillTypes.SOLID,
        }}
        onChangeColor={onChangeHighlightedTextColor}
        onBackButton={(): void => {
          setPanelToShow(DateStylesPanel.DEFAULT);
        }}
        onClose={onClose}
      />
    );
  };

  const getPanels = (): ReactElement => {
    switch (panelToShow) {
      case DateStylesPanel.HIGHLIGHTED_BACKGROUND:
        return getHighlightedBackgroundColorPanel();
      case DateStylesPanel.HIGHLIGHTED_TEXT:
        return getHighlightedTextColorPanel();
      case DateStylesPanel.DEFAULT:
      default:
        return getDefaultPanel();
    }
  };

  return getPanels();
}
